import Add from "@mui/icons-material/Add";
import { Box, Button, Chip, CircularProgress, styled } from "@mui/material";
import { useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useToken } from "../../server/auth/use-token";
import { serviceURL } from "../../utils/service-url";
import { SURVEY_FORMAT } from "./content-type-select";

import type { CreateContentRequest } from "../multiformat/store/types";
import type { FC } from "react";

export const CreateContentFileUpload: FC = () => {
  const getToken = useToken();
  const [loading, setLoading] = useState<boolean>(false);

  const { control, setValue } = useFormContext<CreateContentRequest>();
  const [fileValue, format] = useWatch({ control, name: ["file", "format"] });

  const handleSubmit = useCallback(
    async (files?: FileList | null) => {
      if (!files || files.length === 0) {
        console.error("No file found");
        return;
      }

      if (files.length > 1) {
        console.error("Can only upload one file at a time");
        return;
      }
      const file = files[0]!;

      if (file.size > 50 * 1024 * 1024) {
        toast.error("This file is too large - please try again with a file under 50 MB");

        console.error("File size exceeds 50 MB");
        return;
      }

      setLoading(true);

      const body = new FormData();
      body.append("purpose", "user_data");
      body.append("file", file);

      try {
        const response = await fetch(`${serviceURL("autopilot")}file`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
          body,
        });

        if (!response.ok || !response.body) {
          console.error("Failed to upload file");
          toast.error("Failed to upload file");
          return;
        }

        const r: { id: string } = await response.json();

        setValue("file", { id: r.id, name: file.name });
      } finally {
        // Brings the upload button back after a failure, so the user can try again
        setLoading(false);
      }
    },
    [getToken, setValue],
  );

  return (
    <Box
      sx={{
        position: "absolute",
        bottom: 0,
        left: 8,
        padding: 1,
      }}
    >
      {loading ? (
        <CircularProgress size={18} />
      ) : !fileValue ? (
        <Button
          variant="text"
          startIcon={<Add />}
          data-analytics-id="create-content-upload-file-button"
          sx={{
            p: 0,
            textTransform: "none",
            color: "text.secondary",
            fontWeight: "400",
            "&:hover": {
              backgroundColor: "transparent",
              textDecoration: "underline",
            },
          }}
        >
          {format === SURVEY_FORMAT
            ? "Upload a pdf (max 50 MB)"
            : "Upload a pdf (max 50 MB) to summarize it"}
          <HiddenInput
            type="file"
            accept=".pdf"
            onChange={(event) => {
              void handleSubmit(event.target.files).catch(console.error);
            }}
          />
        </Button>
      ) : (
        <Chip
          size="small"
          color="primary"
          label={fileValue?.name}
          onDelete={() => setValue("file", undefined)}
        />
      )}
    </Box>
  );
};

const HiddenInput = styled("input")({
  position: "absolute",
  width: "100%",
  height: "100%",
  opacity: 0,
  cursor: "pointer",
});
